/**
 * QUIPWA — sql.js loader
 *
 * sql.js is shipped from `public/sql.js-wasm/` (so the service worker precaches
 * the WASM next to the app shell) and loaded once per page. Every database in
 * the app — the replicated state DB, the device-local DB and each deck's
 * collection DB — shares this single engine instance.
 *
 * The *provider* is swappable so unit tests can run real SQLite in Node
 * (see `src/test/sqljs-node.ts`) without a DOM or a `<script>` tag.
 */

import type { SqlJsStatic } from 'sql.js'

export type SqlJsProvider = () => Promise<SqlJsStatic>

interface SqlJsWindow extends Window {
  initSqlJs?: (config?: { locateFile: () => string }) => Promise<SqlJsStatic>
}

const SCRIPT_URL = '/sql.js-wasm/sql-wasm.js'
const WASM_URL = '/sql.js-wasm/sql-wasm.wasm'

/** Browser provider: injects the UMD script once, then instantiates the WASM. */
export const browserSqlJsProvider: SqlJsProvider = async () => {
  const w = window as SqlJsWindow
  if (!w.initSqlJs) {
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script')
      script.src = SCRIPT_URL
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('No se pudo cargar el motor SQLite (sql-wasm.js)'))
      document.head.appendChild(script)
    })
  }
  const init = w.initSqlJs
  if (typeof init !== 'function') {
    throw new Error('initSqlJs no está disponible tras cargar sql-wasm.js')
  }
  return init({ locateFile: () => WASM_URL })
}

let provider: SqlJsProvider = browserSqlJsProvider
let instance: Promise<SqlJsStatic> | null = null

/**
 * Replace the engine provider. Resets the cached instance, so call it before
 * the first database is opened (tests do it in their setup file).
 */
export function setSqlJsProvider(next: SqlJsProvider): void {
  provider = next
  instance = null
}

/** The shared sql.js engine. A failed load is not cached, so it can be retried. */
export function getSqlJsSingleton(): Promise<SqlJsStatic> {
  if (!instance) {
    instance = provider().catch((err: unknown) => {
      instance = null
      throw err
    })
  }
  return instance
}
